import React from 'react';
import { AlertTriangle, Shield, Info } from 'lucide-react';

interface SecurityMessageProps {
  type: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  suggestions?: string[];
}

export const SecurityMessage: React.FC<SecurityMessageProps> = ({
  type,
  title,
  message,
  suggestions
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <Shield className="w-5 h-5 text-red-400" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getContainerClass = () => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-950/35 border-yellow-800/50 text-yellow-200';
      case 'error':
        return 'bg-red-950/35 border-red-800/50 text-red-200';
      case 'info':
        return 'bg-blue-950/35 border-blue-800/50 text-blue-200';
      default:
        return 'bg-blue-950/35 border-blue-800/50 text-blue-200';
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getContainerClass()}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3">
          {getIcon()}
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium mb-1">
            {title}
          </h3>
          <p className="text-sm mb-2">
            {message}
          </p>
          {suggestions && suggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium mb-1">Sugerencias:</p>
              <ul className="text-xs space-y-1">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Component for showing security status during registration
export const RegistrationSecurityStatus: React.FC<{
  isChecking: boolean;
  securityMessage?: SecurityMessageProps;
}> = ({ isChecking, securityMessage }) => {
  if (isChecking) {
    return (
      <div className="mb-4">
        <SecurityMessage
          type="info"
          title="Verificando seguridad"
          message="Estamos verificando la información de registro para garantizar la seguridad de todos los usuarios."
        />
      </div>
    );
  }

  if (securityMessage) {
    return (
      <div className="mb-4">
        <SecurityMessage {...securityMessage} />
      </div>
    );
  }

  return null;
};
